/* global Swiper */

const REST_BASE = window.wpApiSettings?.root
	? window.wpApiSettings.root.replace( /\/$/, '' ) + '/chalk/v1/vacancies'
	: '/wp-json/chalk/v1/vacancies';

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function debounce( fn, ms ) {
	let timer;
	return ( ...args ) => {
		clearTimeout( timer );
		timer = setTimeout( () => fn( ...args ), ms );
	};
}

async function fetchVacancies( blockId, params = {} ) {
	const url = new URL( REST_BASE, window.location.origin );
	url.searchParams.set( 'block_id', blockId );
	Object.entries( params ).forEach( ( [ k, v ] ) => {
		if ( v !== '' && v !== null && v !== undefined ) {
			url.searchParams.set( k, v );
		}
	} );

	const res = await fetch( url.toString() );
	if ( ! res.ok ) throw new Error( `chalk-nursery-vacancies: fetch failed ${ res.status }` );
	return res.json(); // { html, hasMore, total }
}

function setLoading( wrapper, loading ) {
	wrapper.classList.toggle( 'chalk-nursery-vacancies--loading', loading );
}

function replaceGrid( wrapper, html ) {
	const grid = wrapper.querySelector( '.chalk-nursery-vacancies__grid' );
	if ( grid ) grid.innerHTML = html;
}

function appendGrid( wrapper, html ) {
	const grid = wrapper.querySelector( '.chalk-nursery-vacancies__grid' );
	if ( ! grid ) return;
	const tmp = document.createElement( 'div' );
	tmp.innerHTML = html;
	Array.from( tmp.children ).forEach( ( child ) => grid.appendChild( child ) );
}

function syncLoadMoreButton( wrapper, hasMore, nextPage ) {
	const btn = wrapper.querySelector( '.chalk-nursery-vacancies__load-more' );
	if ( ! btn ) return;
	if ( hasMore ) {
		btn.dataset.page = nextPage;
		btn.style.display = '';
	} else {
		btn.style.display = 'none';
	}
}

// ---------------------------------------------------------------------------
// Grid: filters + load more
// ---------------------------------------------------------------------------

function initGrid( wrapper ) {
	const blockId    = wrapper.dataset.blockId;
	const blogId     = wrapper.dataset.blogId || '1';
	const filterMode = wrapper.dataset.filterMode || 'single'; // 'network' | 'single'
	if ( ! blockId ) return;

	const searchEl   = wrapper.querySelector( '.chalk-nursery-vacancies__search' );
	const nurseryEl  = wrapper.querySelector( '.chalk-nursery-vacancies__nursery-filter' ); // main site only
	const roleEl     = wrapper.querySelector( '.chalk-nursery-vacancies__role-filter' );
	const locationEl = wrapper.querySelector( '.chalk-nursery-vacancies__location-filter' );
	const loadMoreEl = wrapper.querySelector( '.chalk-nursery-vacancies__load-more' );

	const getState = () => ( {
		search:        wrapper.dataset.search        || '',
		// Nursery group: only present in network mode (main site).
		nursery_id:    wrapper.dataset.nurseryId     || '',
		// Role filter: name in network mode, ID in single-site mode.
		role_name:     filterMode === 'network' ? ( wrapper.dataset.roleName     || '' ) : '',
		role_id:       filterMode === 'network' ? '' : ( wrapper.dataset.roleId       || '' ),
		// Nursery post filter: title in network mode, post ID in single-site mode.
		nursery_post_name: filterMode === 'network' ? ( wrapper.dataset.nurseryPostName || '' ) : '',
		nursery_post_id:   filterMode === 'network' ? '' : ( wrapper.dataset.nurseryPostId   || '' ),
		page:          parseInt( wrapper.dataset.page || '1', 10 ),
	} );

	const setState = ( patch ) => Object.assign( wrapper.dataset, {
		search:       patch.search        ?? wrapper.dataset.search       ?? '',
		nurseryId:    patch.nursery_id    ?? wrapper.dataset.nurseryId    ?? '',
		roleName:     patch.role_name     ?? wrapper.dataset.roleName     ?? '',
		roleId:       patch.role_id       ?? wrapper.dataset.roleId       ?? '',
		nurseryPostName: patch.nursery_post_name ?? wrapper.dataset.nurseryPostName ?? '',
		nurseryPostId:   patch.nursery_post_id   ?? wrapper.dataset.nurseryPostId   ?? '',
		page:         patch.page          ?? wrapper.dataset.page          ?? '1',
	} );

	const filterAndReplace = async () => {
		setState( { page: 1 } );
		setLoading( wrapper, true );
		try {
			const data = await fetchVacancies( blockId, { ...getState(), page: 1, blog_id: blogId } );
			replaceGrid( wrapper, data.html );
			syncLoadMoreButton( wrapper, data.hasMore, 2 );
		} catch ( e ) {
			console.error( e );
		} finally {
			setLoading( wrapper, false );
		}
	};

	const debouncedFilter = debounce( filterAndReplace, 400 );

	if ( searchEl ) {
		searchEl.addEventListener( 'input', () => {
			setState( { search: searchEl.value } );
			debouncedFilter();
		} );
	}

	// Nursery group (main site / network mode only).
	if ( nurseryEl ) {
		nurseryEl.addEventListener( 'change', () => {
			setState( { nursery_id: nurseryEl.value } );
			filterAndReplace();
		} );
	}

	if ( roleEl ) {
		roleEl.addEventListener( 'change', () => {
			if ( filterMode === 'network' ) {
				setState( { role_name: roleEl.value, role_id: '' } );
			} else {
				setState( { role_id: roleEl.value, role_name: '' } );
			}
			filterAndReplace();
		} );
	}

	if ( locationEl ) {
		locationEl.addEventListener( 'change', () => {
			if ( filterMode === 'network' ) {
				setState( { nursery_post_name: locationEl.value, nursery_post_id: '' } );
			} else {
				setState( { nursery_post_id: locationEl.value, nursery_post_name: '' } );
			}
			filterAndReplace();
		} );
	}

	if ( loadMoreEl ) {
		loadMoreEl.addEventListener( 'click', async () => {
			const page = parseInt( loadMoreEl.dataset.page || '2', 10 );
			const state = getState();
			loadMoreEl.disabled = true;
			setLoading( wrapper, true );
			try {
				const data = await fetchVacancies( blockId, { ...state, page, blog_id: blogId } );
				appendGrid( wrapper, data.html );
				syncLoadMoreButton( wrapper, data.hasMore, page + 1 );
			} catch ( e ) {
				console.error( e );
			} finally {
				loadMoreEl.disabled = false;
				setLoading( wrapper, false );
			}
		} );
	}
}

// ---------------------------------------------------------------------------
// Slider: Swiper init
// ---------------------------------------------------------------------------

function initSlider( el, index ) {
	const speedModifier = index * 300;
	const itemsMobile  = +el.dataset.itemsMobile  || 1;
	const itemsTablet  = +el.dataset.itemsTablet  || 2;
	const itemsDesktop = +el.dataset.itemsDesktop || 3;
	const speed        = +el.dataset.speed        || 300;
	const delay        = +el.dataset.delay        || 6000;
	const spaceBetween = +el.dataset.spaceBetween || 24;
	const enableLoop   = el.dataset.enableLoop    === 'true';
	const enableNav    = el.dataset.enableNav     === 'true';
	const enablePaging = el.dataset.enablePagination === 'true';

	const config = {
		slidesPerView: itemsMobile,
		speed,
		spaceBetween,
		loop: enableLoop,
		autoplay: {
			delay: delay + speedModifier,
			pauseOnMouseEnter: true,
		},
		breakpoints: {
			840:  { slidesPerView: itemsTablet },
			1200: { slidesPerView: itemsDesktop },
		},
	};

	if ( enablePaging ) {
		config.pagination = {
			el: el.querySelector( '.chalk-nursery-vacancies__pagination' ),
			clickable: true,
		};
	}

	if ( enableNav ) {
		config.navigation = {
			nextEl: el.querySelector( '.chalk-nursery-vacancies__btn-next' ),
			prevEl: el.querySelector( '.chalk-nursery-vacancies__btn-prev' ),
		};
	}

	new Swiper( el, config );
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

document.addEventListener( 'DOMContentLoaded', () => {
	document
		.querySelectorAll( '.chalk-nursery-vacancies--grid' )
		.forEach( initGrid );

	document
		.querySelectorAll( '.chalk-nursery-vacancies--slider.swiper' )
		.forEach( ( el, i ) => initSlider( el, i ) );
} );
